
import EoTransitionAccordion from '../EoTransitionAccordion/EoTransitionAccordion'

export default {
  name: 'EoInput',
  components: { EoTransitionAccordion },
  inheritAttrs: false, // place additional attrs not on container element, but on <input>
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    /**
     * Value of the field
     */
    value: {
      type: [String, Number],
      default: '',
    },
    /**
     * Guiding label above the field
     */
    label: {
      type: String,
      default: null,
      required: true,
    },
    /**
     * Visually hide the label, but make it visible for screen readers
     */
    labelHidden: {
      type: Boolean,
      default: false,
    },
    /**
     * Placeholder for value in the field
     */
    placeholder: {
      type: String,
      default: null,
    },
    /**
     * Description of the field (a hint)
     */
    description: {
      type: String,
      default: null,
    },
    /**
     * The name attribute
     */
    name: {
      type: String,
      default: null,
    },
    /**
     * Style: remove spacing between input and before and after slots
     */
    group: {
      type: Boolean,
      default: false,
    },
    /**
     * Textual prefix before input
     */
    prefix: {
      type: String,
      default: null,
    },
    /**
     * Textual suffix before input
     */
    suffix: {
      type: String,
      default: null,
    },
    /**
     * Autofocus this field on page load
     */
    autofocus: {
      type: Boolean,
      default: false,
    },
    /**
     * Show the field in error state
     */
    error: {
      type: Boolean,
      default: false,
    },
    /**
     * Array of messages to display beneath the field
     */
    errorMessages: {
      type: Array,
      default: () => [],
    },
    /**
     * Prevent user interaction with the field
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Minimum width (1-12 OR pixels)
     */
    minWidth: {
      type: [String, Number],
      default: null,
      validator: (value) => {
        if (Number(value)) {
          return 0 <= Number(value) && Number(value) <= 12
        }
        return true
      },
    },
    /**
     * Maximum width (1-12 OR pixels)
     */
    maxWidth: {
      type: [String, Number],
      default: null,
      validator: (value) => {
        if (Number(value)) {
          return 0 <= Number(value) && Number(value) <= 12
        }
        return true
      },
    },
    /**
     * Style: less padding to shrink the field
     */
    compact: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: text and border color
     */
    textColor: {
      type: String,
      default: null,
    },
    /**
     * Style: background color
     */
    color: {
      type: String,
      default: null,
    },
    /**
     * Style: hide the border
     */
    borderHidden: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      id: null,
      isFocus: false,
      isDragHover: false,
    }
  },
  computed: {
    classes () {
      return {
        'eo-input': true,
        'is-focus': this.isFocus && !this.disabled,
        'is-empty': this.value == null || this.value == '',
        'is-readonly': this.$attrs.readonly,
        'is-label-hidden': this.labelHidden,
        'is-disabled': this.disabled,
        'is-error': this.error,
        'is-drag-hover': this.isDragHover,
        'has-before': !!this.$slots.before,
        'has-after': !!this.$slots.after,
        'has-placeholder': !!this.placeholder,
        'is-group': this.group,
        'is-compact': this.compact,
        'is-hidden-border': this.borderHidden,
        ['is-type-' + this.$attrs.type]: !!this.$attrs.type,
        ['text--' + this.textColor]: !!this.textColor,
      }
    },
    styles () {
      return {
        minWidth: this.convertWidthToCss(this.minWidth),
        maxWidth: this.convertWidthToCss(this.maxWidth),
      }
    },
    computedName () {
      return this.name || this.id
    },
    messagesId () {
      return `messages-${this.id}`
    },
  },
  mounted () {
    this.id = this.$attrs.id || `input-${(this.name ? this.name + '-' : '') + this._uid}`
  },
  methods: {
    convertWidthToCss (size) {
      /**
       * Convert size to pixels when a integer has been given
       * @property {String} size - number from 1 to 12 or a fixed amount of 'px'
       */
      if (!size) return null
      if (Number(size) && 0 < size && size <= 12) {
        return (100 / 12 * size) + '%'
      }
      if (typeof size === 'string' && (size.includes('px') || size.includes('calc'))) {
        return size
      }
      return size + 'px'
    },
    trackFocus (value) {
      this.isFocus = value
    },
    focus () {
      this.$refs.input.focus()
    },
    blur () {
      this.$refs.input.blur()
    },
    onInput (e) {
      /**
       * Emitted when the value has been altered
       * @event input
       */
      this.$emit('input', e.target.value)
    },
    onChange (e) {
      /**
       * Emitted when the value has been altered
       * @event change
       */
      this.$emit('change', e)
    },
    onFocus (e) {
      /**
       * Emitted when the user focusses on the field
       * @event focus
       */
      this.$emit('focus', e)
      this.trackFocus(true)
    },
    onBlur (e) {
      /**
        * Emitted when the user removes focus from the field
        * @event blur
        */
      this.$emit('blur', e)
      this.trackFocus(false)
    },
    onDragenter () {
      this.isDragHover = true
    },
    onDragleave () {
      this.isDragHover = false
    },
    onKeydown (e) {
      /**
        * Emitted when the user types in the field
        * @event keydown
        */
      if (this.$attrs.type == 'number') {
        // Prevent user from typing these characters inside a number field
        ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()
      }
      this.$emit('keydown', e)
    },
    onKeyup (e) {
      this.$emit('keyup', e)
    },
  },
}
